import { Select } from '@/components/Select';
import { WithLoader } from '@/components/WithLoader';
import { NaturalDemandSeasonalityChart } from '@/dashboards/CampaignOptimization/components/NaturalDemandSeasonality/NaturalDemandSeasonalityChart';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { getPeriodOptionsForModel } from '@/utils/model';
import { Tooltip, Typography } from '@analytical-alley/ui';
import { IconExclamationCircle } from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';

export type StructuredSeasonalityData = {
    period: number;
    contributionIndex: number;
};

const OPTION_LABELS = {
    daily: 'Weekdays',
    weekly: 'Weekly',
    monthly: 'Monthly',
};

export const NaturalDemandSeasonality = () => {
    const {
        data: { optimized },
        isDataLoading,
        isDataFetching,
    } = useOptimizationSession();

    const {
        project: { interval },
    } = useDashboardGlobalContext();

    const availablePeriodOptions = getPeriodOptionsForModel(interval, [
        'daily',
        'weekly',
        'monthly',
    ]);

    const [selectedOption, setSelectedOption] = useState(
        availablePeriodOptions[1],
    );

    const data = useMemo(() => {
        if (optimized) {
            switch (selectedOption) {
                case 'daily':
                    return {
                        plan: optimized.naturalDemand.daily,
                        average: optimized.weekDayAverage,
                    };
                case 'monthly':
                    return {
                        plan: optimized.naturalDemand.monthly,
                        average: optimized.monthlyAverage,
                    };
                case 'weekly':
                    return {
                        plan: optimized.naturalDemand.weekly,
                        average: optimized.weeklyAverage,
                    };
            }
        }
        return {
            plan: [],
            average: 0,
        };
    }, [selectedOption, optimized]);

    const structuredSeasonalityData: StructuredSeasonalityData[] =
        useMemo(() => {
            if (!data?.plan) {
                return [];
            }
            return data.plan.map((item, index) => {
                return {
                    period: index + 1,
                    contributionIndex: item,
                };
            });
        }, [data.plan]);

    return (
        <div className="relative">
            <WithLoader isLoading={isDataLoading} isFetching={isDataFetching}>
                <div className="dark:bg-white/10 rounded-3xl shadow-lg p-6">
                    <div className="flex mb-4 gap-2">
                        <Typography variant="titleS">
                            Natural Demand Seasonality
                        </Typography>
                        <span className="grow" />
                        <Select
                            id={'periodicity'}
                            className="w-36"
                            selectedValue={selectedOption}
                            onSelectChange={(value) => {
                                setSelectedOption(value);
                            }}
                            dismissOnClick
                            label="Periodicity"
                            compact
                        >
                            {availablePeriodOptions.map((periodName) => {
                                return (
                                    <Select.Option
                                        key={periodName}
                                        label={OPTION_LABELS[periodName]}
                                        value={periodName}
                                    />
                                );
                            })}
                        </Select>

                        <Tooltip placement="bottom-end">
                            <Tooltip.Trigger>
                                <span className="cursor-pointer">
                                    <IconExclamationCircle className="w-5" />
                                </span>
                            </Tooltip.Trigger>
                            <Tooltip.Content className="tooltip-content max-w-xl">
                                <Typography variant="bodyM" color="black">
                                    Natural demand represents the baseline level of
                                    consumer demand for a product or service,
                                    existing independently of any active media,
                                    owned marketing, promotions, or other
                                    interventions.
                                </Typography>
                                <Typography variant="bodyM" color="black">
                                    It reflects inherent consumer interest and
                                    purchasing behavior that would continue even
                                    without marketing campaigns.
                                </Typography>
                            </Tooltip.Content>
                        </Tooltip>
                    </div>

                    <div className="relative">
                        <div className="flex justify-between">
                            <NaturalDemandSeasonalityChart
                                key={selectedOption}
                                periodicity={selectedOption!}
                                data={structuredSeasonalityData}
                            />
                        </div>
                    </div>
                </div>
            </WithLoader>
        </div>
    );
};
